<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealListTable from 'dashboard/components-next/Deals/DealListTable.vue';
import DealForm from 'dashboard/components-next/Deals/DealForm.vue';

const { t } = useI18n();
const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();

const deals = computed(() => dealsStore.getDealsList);
const uiFlags = computed(() => dealsStore.uiFlags);
const meta = computed(() => dealsStore.meta);

const selectedDeal = ref(null);
const showFormModal = ref(false);

const openCreateForm = () => {
  selectedDeal.value = null;
  showFormModal.value = true;
};

const openEditForm = deal => {
  selectedDeal.value = deal;
  showFormModal.value = true;
};

const handleSubmit = async dealData => {
  try {
    if (selectedDeal.value) {
      await dealsStore.update({ id: selectedDeal.value.id, ...dealData });
      useAlert(t('DEALS.EDIT.SUCCESS_MESSAGE'));
    } else {
      await dealsStore.create(dealData);
      useAlert(t('DEALS.CREATE.SUCCESS_MESSAGE'));
    }
    showFormModal.value = false;
  } catch (error) {
    useAlert(error.message || t('DEALS.ERROR_MESSAGE'));
  }
};

const handleDelete = async deal => {
  try {
    await dealsStore.delete(deal.id);
    useAlert(t('DEALS.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('DEALS.ERROR_MESSAGE'));
  }
};

const onPageChange = page => {
  dealsStore.get({ page });
};

onMounted(async () => {
  await pipelinesStore.get();
  await dealsStore.get({ page: 1 });
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="flex items-center justify-between mx-auto max-w-5xl py-4">
        <h2 class="text-xl font-medium truncate text-n-slate-12">
          {{ t('DEALS.HEADER') }}
        </h2>
        <button
          class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md transition-colors"
          @click="openCreateForm"
        >
          <i class="i-lucide-plus size-4" />
          {{ t('DEALS.CREATE.BUTTON_TEXT') }}
        </button>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6">
      <div class="mx-auto max-w-5xl py-4">
        <Spinner v-if="uiFlags.fetchingList" />
        <DealListTable
          v-else
          :deals="deals"
          @edit="openEditForm"
          @delete="handleDelete"
        />
      </div>
    </main>

    <div v-if="meta.totalCount > 15" class="px-6 py-4">
      <div class="mx-auto max-w-5xl flex justify-center">
        <woot-pagination
          :current-page="Number(meta.page)"
          :total-count="meta.totalCount"
          :page-size="15"
          @page-change="onPageChange"
        />
      </div>
    </div>

    <DealForm
      v-if="showFormModal"
      :deal="selectedDeal"
      @submit="handleSubmit"
      @close="showFormModal = false"
    />
  </div>
</template>
